import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Group, Loader, Menu, Text, UnstyledButton } from "@mantine/core"
import { useMemo } from "react"
import { LOCALE_LABELS, SUPPORTED_LOCALES, type TLocale } from "../../lib/i18n"
import type { IProps } from "./interfaces"

function localeLabel(code: string): string {
  return (LOCALE_LABELS as Record<string, string | undefined>)[code] ?? code.toUpperCase()
}

/**
 * Per-meeting language switcher (Phase 4 of multi-language).
 *
 * Surfaces three categories:
 *  - source: the locale the meeting was originally analysed in (instant)
 *  - cached: locales already translated on the meeting row (instant)
 *  - available: supported UI locales not yet translated (lazy — kicks off
 *    a backend translation on click; first viewer pays a small LLM cost,
 *    subsequent viewers hit the cache)
 *
 * Clicking the source locale or a cached locale never costs anything.
 * The component leaves the cost decision to the user — by default the
 * meeting opens in its source locale even if the UI is in another
 * language, so we don't auto-burn LLM credits on every page view.
 */
export function MeetingLanguageSwitcher({
  sourceLocale,
  availableTranslations,
  currentLocale,
  isTranslating,
  onLocaleChange,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  // Build the option list: source first, then any cached translations,
  // then the rest of the supported UI locales (deduplicated). Cached entries
  // can be either fresh ("ready") or out-of-date ("stale") relative to the
  // current analysis_version — Phase 5 surfaces both with distinct badges.
  const options = useMemo(() => {
    const seen = new Set<string>()
    const list: Array<{ code: string; kind: "source" | "cached" | "stale" | "available" }> = []

    seen.add(sourceLocale)
    list.push({ code: sourceLocale, kind: "source" })

    for (const entry of availableTranslations) {
      if (!seen.has(entry.locale)) {
        seen.add(entry.locale)
        list.push({ code: entry.locale, kind: entry.stale ? "stale" : "cached" })
      }
    }

    for (const code of SUPPORTED_LOCALES as readonly string[]) {
      if (!seen.has(code)) {
        seen.add(code)
        list.push({ code, kind: "available" })
      }
    }

    return list
  }, [sourceLocale, availableTranslations])

  const current = options.find((o) => o.code === currentLocale) ?? options[0]
  const summaryName = localeLabel(current.code)

  return (
    <Menu shadow="md" position="bottom-end" disabled={isTranslating}>
      <Menu.Target>
        <UnstyledButton
          aria-label={i18n._(msg`Change summary language`)}
          data-testid="summary-language-switcher"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 10px",
            border: "1px solid var(--mantine-color-default-border)",
            borderRadius: "var(--mantine-radius-sm)",
            cursor: isTranslating ? "wait" : "pointer",
          }}
        >
          {isTranslating ? <Loader size={12} /> : null}
          <Text size="sm" fw={500}>
            <Trans>Summary in: {summaryName}</Trans>
          </Text>
          <Text size="xs" c="dimmed">
            ▾
          </Text>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>
          <Trans>Summary language</Trans>
        </Menu.Label>
        {options.map((opt) => {
          const isActive = opt.code === currentLocale
          return (
            <Menu.Item
              key={opt.code}
              onClick={() => onLocaleChange(opt.code)}
              disabled={isActive}
              rightSection={
                <Group gap={4} wrap="nowrap">
                  {opt.kind === "source" && (
                    <Badge size="xs" color="gray" variant="light">
                      <Trans>source</Trans>
                    </Badge>
                  )}
                  {opt.kind === "cached" && (
                    <Badge size="xs" color="success" variant="light">
                      <Trans>ready</Trans>
                    </Badge>
                  )}
                  {opt.kind === "stale" && (
                    <Badge size="xs" color="warning" variant="light" title={i18n._(msg`Click to refresh translation`)}>
                      <Trans>stale</Trans>
                    </Badge>
                  )}
                  {opt.kind === "available" && (
                    <Badge size="xs" color="info" variant="outline">
                      <Trans>translate</Trans>
                    </Badge>
                  )}
                </Group>
              }
            >
              <Text size="sm">{localeLabel(opt.code)}</Text>
            </Menu.Item>
          )
        })}
      </Menu.Dropdown>
    </Menu>
  )
}

export type { TLocale }
